import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { type useReschedule } from '../use-reschedule';

/**
 * Nhờ AI xếp lại lịch — APM-1313.
 *
 * ## Vì sao nút chỉ hiện khi có gì để xếp
 *
 * Một nút "Nhờ AI xếp lại" luôn sáng trên mọi dự án là một lời mời tiêu tiền
 * vào một câu hỏi không ai hỏi. Nó xuất hiện khi `schedule-impact` thật sự có
 * việc mất người hoặc dự án đang trượt — tức đúng lúc câu hỏi có nghĩa.
 *
 * ## Vì sao diff duyệt từng dòng
 *
 * ADR-005: người bấm chịu trách nhiệm. Duyệt trọn gói là cách nhanh nhất để một
 * phương án tám nước đi được bấm qua mà không ai đọc nước thứ sáu — và nước thứ
 * sáu là nước chuyển việc của người vừa nghỉ sang người sắp nghỉ.
 *
 * ## Không xếp nổi cũng là một câu trả lời
 *
 * Khi `shortfall` có mặt, khối này hiện "cần thêm 1 Backend trong 10 ngày, hoặc
 * giãn hạn 6 ngày" — chứ không hiện một danh sách rỗng và để PM đoán rằng AI
 * hỏng.
 */
export function ReschedulePanel({
  canApply,
  state,
}: {
  /** `ai_suggestion:approve` — không có thì vẫn xem được phương án, chỉ không áp. */
  readonly canApply: boolean;
  /**
   * Lượt chạy, do **chỗ gọi** giữ — APM-1313b.
   *
   * Nút bấm nay ở thanh công cụ cạnh "Việc mới", tức ở một cây component khác;
   * state phải nằm ở tổ tiên chung thì bấm ở trên mới ra kết quả ở đây. Xem
   * `useReschedule`.
   */
  readonly state: ReturnType<typeof useReschedule>;
}): ReactNode {
  const { t } = useTranslation();
  const { suggestion, setSuggestion, chosen, setChosen, plan, apply } = state;

  /*
    Chỉ hiện khi **đã có phương án** — APM-1313b.

    Trước đây khối này hiện sẵn (kèm tiêu đề và một câu mô tả) ngay khi dự án
    có việc mất người, vì nó còn phải chứa nút bấm. Nút nay ở thanh công cụ,
    nên tiêu đề đứng lại một mình chỉ là một cái hộp rỗng nói lại đúng thứ nút
    kia vừa nói. Đổi điều kiện theo: có kết quả thì mới có gì để đọc.
  */
  if (suggestion === null) return null;

  return (
    <section className="flex flex-col gap-3 rounded-DEFAULT border border-border p-3">
      {plan !== null && (
        <>
          {plan.summary !== '' && (
            <p className="whitespace-pre-line text-xs text-muted-foreground">{plan.summary}</p>
          )}

          {plan.moves.length > 0 ? (
            <>
              <ul className="flex flex-col gap-1">
                {plan.moves.map((move, index) => (
                  <li
                    key={`${move.taskKey}-${index}`}
                    className="flex flex-wrap items-start gap-2 rounded-DEFAULT border border-border px-3 py-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      className="ios-check ios-check-select"
                      checked={chosen.has(index)}
                      aria-label={move.taskKey}
                      onChange={(event) =>
                        setChosen((current) => {
                          const next = new Set(current);
                          if (event.target.checked) next.add(index);
                          else next.delete(index);
                          return next;
                        })
                      }
                    />

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{move.taskKey}</span>
                        {move.newAssigneeName !== null && (
                          <span className="text-xs">
                            {t('reschedule.toPerson', { name: move.newAssigneeName })}
                          </span>
                        )}
                        {move.newStartDate !== null && move.newDueDate !== null && (
                          <span className="text-xs tabular-nums">
                            {t('reschedule.toDates', {
                              from: move.newStartDate,
                              to: move.newDueDate,
                            })}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{move.reason}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  disabled={!canApply || chosen.size === 0 || apply.isPending}
                  title={canApply ? undefined : t('reschedule.needApprove')}
                  onClick={() => apply.mutate()}
                >
                  {t('reschedule.apply', { count: chosen.size })}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setSuggestion(null)}>
                  {t('reschedule.discard')}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">{t('reschedule.noPlan')}</p>
          )}

          {plan.shortfall !== null && (
            <div className="flex flex-col gap-1 rounded-DEFAULT border border-rag-amber/40 bg-rag-amber/5 p-3 text-xs">
              <p className="font-medium">{t('reschedule.shortfallTitle')}</p>
              <p>{plan.shortfall.explanation}</p>
              <ul className="list-disc pl-4">
                {plan.shortfall.needPeople.map((gap) => (
                  <li key={`${gap.skill}-${gap.days}`}>
                    {t('reschedule.needPeople', {
                      people: gap.people,
                      skill: gap.skill,
                      days: gap.days,
                    })}
                  </li>
                ))}
                {plan.shortfall.extendDays > 0 && (
                  <li>{t('reschedule.extendDays', { days: plan.shortfall.extendDays })}</li>
                )}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}
